import { Component, AfterViewInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ExamearquivoService } from 'app/service/api/exame/examearquivo.service';
import { ToastrService } from 'ngx-toastr';

interface ArquivoInterface {
  IdArquivo: string;
  NomeOriginal: string;
}

@Component({
  selector: 'app-local',
  templateUrl: './local.component.html',
  styleUrls: ['./local.component.scss']
})
export class LocalComponent implements AfterViewInit {

  private ohifViewerScriptUrl = 'https://unpkg.com/@ohif/viewer@4.12.51/dist/index.umd.js';

  constructor(
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private exameArqSvc: ExamearquivoService
  ) { }

  ngAfterViewInit() {
    this.initViewerProcess();
  }

  private async getFileSource(arquivo: ArquivoInterface): Promise<string> {
    try {
      const res = await this.exameArqSvc.download(arquivo.IdArquivo).toPromise();
      if (res) {
        return res.name;
      }
      throw Error("Não foi possível acessar o arquivo da imagem.");
    } catch (error) {
      this.downloadFileDirectly(arquivo.IdArquivo, arquivo.NomeOriginal);
      throw error;
    }
  }

  private initViewerProcess() {
    this.route.fragment.subscribe({
      next: async (fragment) => {
        if (fragment) {
          try {
            const arquivo = this.decodeFragment(fragment);
            const fileSource = await this.getFileSource(arquivo);

            this.isDicomFile(arquivo.NomeOriginal)
              ? this.initializeOhifViewer(arquivo, fileSource)
              : this.displayFile(fileSource, arquivo.NomeOriginal);

          } catch (error) {
            this.initializeOhifViewer();
          }
        } else {
          this.initializeOhifViewer();
        }
      },
      error: () => {
        this.toastr.error('Ocorreu um erro ao iniciar o processo de visualização', 'Erro inesperado');
      }
    });
  }

  private loadOhifScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${this.ohifViewerScriptUrl}"]`)) {
        resolve();
        return;
      }

      const script = this.renderer.createElement('script');
      script.src = this.ohifViewerScriptUrl;
      script.crossOrigin = '';
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);

      this.renderer.appendChild(document.body, script);
    });
  }

  private initializeOhifViewer(arquivo?: ArquivoInterface, fileSource?: string) {
    document.body.innerHTML = "<div id='root'></div>";
    this.loadOhifScript().then(() => {
      const containerId = 'root';
      const componentRenderedOrUpdatedCallback = async () => {
        if (arquivo && fileSource) {
          const file = await this.fetchDicomFile(fileSource, arquivo.NomeOriginal);
          this.addFileToInputElement(file);
        }
      };

      const appConfig = {
        routerBasename: '',
        showStudyList: false,
        filterQueryParam: false,
        maxConcurrentMetadataRequests: 1,
        servers: {
          dicomWeb: [
            {
              name: 'Local',
              wadoUriRoot: '../dicom-json/LIDC-IDRI-0001.json/',
              qidoRoot: '../dicom-json/LIDC-IDRI-0001.json/',
              wadoRoot: '../dicom-json/LIDC-IDRI-0001.json/',
              imageRendering: 'wadors',
              thumbnailRendering: 'thumbnail',
              enableStudyLazyLoad: true,
              supportsFuzzyMatching: false,
              staticWado: true,
              qidoSupportsIncludeField: true
            }
          ]
        }
      };

      if (window['OHIFViewer']) {
        window['OHIFViewer'].installViewer(appConfig, containerId, componentRenderedOrUpdatedCallback);
      } else {
        document.body.innerHTML = "<div id='root'>OHIFViewer is not loaded.</div>";
      }
    });
  }

  private decodeFragment(fragment: string): ArquivoInterface {
    const decoded = atob(fragment);
    return JSON.parse(decoded) as ArquivoInterface;
  }

  private async fetchDicomFile(url: string, fileName: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], fileName, { type: 'application/dicom' });
  }

  private addFileToInputElement(file: File) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    setTimeout(() => {
      const inputElement: HTMLInputElement | null = document.querySelector('input[type="file"]');
      if (inputElement) {
        inputElement.files = dataTransfer.files;
        inputElement.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.toastr.error('Não foi possível carregar o arquivo para visualização.', 'Falha ao carregar arquivo');
      }
    }, 1000);
  }

  private isDicomFile(fileName: string): boolean {
    const dicomExtensions = ['.dcm', '.dicom'];
    return dicomExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  }

  private displayFile(url: string, fileName: string) {
    document.body.innerHTML = "<div id='root' style='width: 100vw; height: 100vh; display: flex; justify-content: center; align-items: center; margin: 0; padding: 0; background-color: black;'></div>";

    const fileExtension = fileName.split('.').pop().toLowerCase();
    const container = document.getElementById('root');
    if (!container) return;

    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        container.innerHTML = `
          <img src="${url}" alt="${fileName}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
    `;
        break;
      case 'pdf':
        container.innerHTML = `
          <embed src="${url}" type="application/pdf" style="width: 100%; height: 100%;" />
        `;
        break;
      default:
        this.downloadFileDirectly(url, fileName);
        break;
    }
  }

  private downloadFileDirectly(url: string, fileName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
